import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject, Optional } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CryptoService } from '../services/crypto.service';
import { SECURITY_CONFIG, SecurityConfig } from '../models/security-config.interface';

/** Decrypts responses using AES keys from headers (X-Request-Context, X-Client-Ref) */
@Injectable()
export class DecryptionInterceptor implements HttpInterceptor {
  constructor(
    private cryptoService: CryptoService,
    @Optional() @Inject(SECURITY_CONFIG) private config: SecurityConfig | null
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const cryptoService = this.cryptoService;
    const config = this.config;

    return (next.handle(req).pipe(
      switchMap((event: any) => {

        // Handle HttpResponse (normal case)
        if (event instanceof HttpResponse) {
          if (config?.enableEncryption === false) {
            return of(event);
          }

          const encryptPatterns = config?.encryptDecryptForEndpoints || ['*'];
          const skipPatterns = config?.skipEncryptDecryptForEndpoints;
          const shouldDecrypt = cryptoService.matchesPattern(req.url, encryptPatterns, skipPatterns);

          if (!shouldDecrypt) {
            return of(event);
          }
          if (config?.publicKeyEndpoint && req.url === config.publicKeyEndpoint) {
            return of(event);
          }

          return decryptResponse(event, cryptoService, config);
        }

        // Handle plain Object response (when another service transforms it)
        if (typeof event === 'object' && event !== null && 'status' in event) {
          // Convert plain object back to HttpResponse for decryption
          const eventObj = event as any;
          const wrappedResponse = new HttpResponse({
            body: eventObj.body !== undefined ? eventObj.body : event,
            headers: eventObj.headers || undefined,
            status: eventObj.status || 200,
            statusText: eventObj.statusText || 'OK',
            url: eventObj.url || req.url
          });
          
          if (config?.enableEncryption === false) {
            return of(wrappedResponse);
          }

          const encryptPatterns = config?.encryptDecryptForEndpoints || ['*'];
          const skipPatterns = config?.skipEncryptDecryptForEndpoints;
          const shouldDecrypt = cryptoService.matchesPattern(req.url, encryptPatterns, skipPatterns);

          if (!shouldDecrypt) {
            return of(wrappedResponse);
          }

          return decryptResponse(wrappedResponse, cryptoService, config);
        }

        // If event is just the response body (encrypted string), check if it looks encrypted
        const eventAsStr = event as string;
        if (typeof eventAsStr === 'string' && eventAsStr.length > 0) {
          if (config?.enableDebugLogging) {
            console.log('[Decryption Interceptor] Event is a string (encrypted body)');
            console.log('[Decryption Interceptor] String length:', eventAsStr.length);
            console.log('[Decryption Interceptor] First 100 chars:', eventAsStr.substring(0, 100));
          }

          // Try to get headers from the request context
          const aesKeyBase64 = req.headers?.get('X-Request-Context') || req.headers?.get('x-request-context');
          const ivBase64 = req.headers?.get('X-Client-Ref') || req.headers?.get('x-client-ref');

          if (config?.enableDebugLogging) {
            console.log('[Decryption Interceptor] AES from request headers:', !!aesKeyBase64);
            console.log('[Decryption Interceptor] IV from request headers:', !!ivBase64);
          }

          if (aesKeyBase64 && ivBase64) {
            return from(
              cryptoService.decryptResponsePayload(eventAsStr, aesKeyBase64, ivBase64)
            ).pipe(
              switchMap(decrypted => {
                if (decrypted) {
                  if (config?.enableDebugLogging) {
                    console.log('[Decryption Interceptor] Successfully decrypted string payload');
                  }
                  return of(decrypted);
                }
                if (config?.enableDebugLogging) {
                  console.log('[Decryption Interceptor] Decryption returned null, returning original event');
                }
                return of(event);
              }),
              catchError(error => {
                if (config?.enableDebugLogging) {
                  console.error('[Decryption Interceptor] Decryption failed:', error);
                }
                return of(event);
              })
            );
          }
        }

        // For all other events (progress, etc), pass through
        if (config?.enableDebugLogging) console.log('[Decryption Interceptor] Not an HttpResponse, skipping');
        return of(event);
      }),
      catchError((error: HttpErrorResponse) => {
      // Handle error responses (4xx, 5xx)
      if (config?.enableEncryption === false) return throwError(() => error);

      const encryptPatterns = config?.encryptDecryptForEndpoints || ['*'];
      const skipPatterns = config?.skipEncryptDecryptForEndpoints;
      const shouldDecrypt = cryptoService.matchesPattern(req.url, encryptPatterns, skipPatterns);

      if (!shouldDecrypt) return throwError(() => error);
      if (config?.publicKeyEndpoint && req.url === config.publicKeyEndpoint) return throwError(() => error);
      // Check if error has encryption headers
      const aesKeyBase64 = error.headers?.get('X-Request-Context') || error.headers?.get('x-request-context');
      const ivBase64 = error.headers?.get('X-Client-Ref') || error.headers?.get('x-client-ref');

      if (!aesKeyBase64 || !ivBase64 || !error.error) {
        return throwError(() => error);
      }

      // Decrypt error response
      return from(
        cryptoService.decryptResponsePayload(
          typeof error.error === 'string' ? error.error : JSON.stringify(error.error),
          aesKeyBase64,
          ivBase64
        ).then(
          decrypted => {
            if (decrypted) {
              // Return decrypted error - spread decrypted data to top level for easier access
              // This allows components to access error.errors[0] instead of error.error.errors[0]
              const decryptedError = new HttpErrorResponse({
                error: decrypted,
                headers: error.headers,
                status: error.status,
                statusText: error.statusText,
                url: error.url || undefined
              });
              
              // Merge decrypted properties to top level of error object
              Object.assign(decryptedError, decrypted);
              
              throw decryptedError; // Throw the decrypted error to be caught by component
            }
            throw error; // Throw original error if decryption returned null
          },
          decryptError => {
            // Decryption failed - throw original error
            throw error;
          }
        )
      );
    })
    )) as Observable<HttpEvent<any>>;
  }
}

/** Helper function to decrypt successful responses */
function decryptResponse(
  event: HttpResponse<any>,
  cryptoService: CryptoService,
  config: SecurityConfig | null
) {
  const aesKeyBase64 = event.headers.get('X-Request-Context') || event.headers.get('x-request-context');
  const ivBase64 = event.headers.get('X-Client-Ref') || event.headers.get('x-client-ref');
  
  if (config?.enableDebugLogging) {
    console.log('[Decryption] Response URL:', event.url);
    console.log('[Decryption] Response Status:', event.status);
    console.log('[Decryption] Has AES Key Header:', !!aesKeyBase64);
    console.log('[Decryption] Has IV Header:', !!ivBase64);
    console.log('[Decryption] Response Body Type:', typeof event.body);
    console.log('[Decryption] Response Body (first 100 chars):', typeof event.body === 'string' ? event.body?.substring(0, 100) : 'N/A');
  }
  
  if (!aesKeyBase64 || !ivBase64 || !event.body) {
    if (config?.enableDebugLogging) {
      console.log('[Decryption] Missing decryption headers or body. Returning response as-is.');
    }
    return of(event);
  }

  return from(
    cryptoService.decryptResponsePayload(event.body as string, aesKeyBase64, ivBase64)
  ).pipe(
    switchMap(decrypted => {
      if (decrypted) {
        if (config?.enableDebugLogging) {
          console.log('[Decryption] Successfully decrypted response');
        }
        return of(event.clone({ body: decrypted }));
      }
      if (config?.enableDebugLogging) {
        console.log('[Decryption] Decryption returned null, returning original event');
      }
      return of(event);
    }),
    catchError(error => {
      if (config?.enableDebugLogging) {
        console.error('[Decryption] Decryption failed with error:', error);
      }
      // Return original encrypted response instead of failing
      return of(event);
    })
  );
}
